import { useState, useEffect, useMemo } from 'react';
import { IAttribute, IAttributeOptions } from 'components/Catalog/Interfaces/IAttribute';
import { ISelectedAttributes, ISelectedSwatch, ISwatchConfig } from 'components/Catalog/Product/Detail/Configurator';
import { ProductSelectedInfo } from '../types/product.types';
import { empty } from '../helpers/empty';

export interface UseProductConfigurationReturn {
    selectedAttributes: ISelectedAttributes[];
    selectAttributes: (value: ISelectedAttributes[]) => void;
    defaultSelectedMainSwatch: ISelectedSwatch | undefined;
    setDefaultSelectedMainSwatch: (swatch: ISelectedSwatch) => void;
    secondaryAttributes: IAttribute[];
    isConfigured: boolean;
    hasConfiguration: boolean;
    setSelectedProductInformation: (selectedProductInformation: ProductSelectedInfo) => void;
}

const findDefaultOption = (filteredAttribute: IAttribute): string => {
    const searchParams = new URLSearchParams(location.search);

    const queryParamValue = searchParams.get(filteredAttribute.code);
    if (empty(queryParamValue)) {
        return filteredAttribute.options.find((option: IAttributeOptions) => option)?.id || '';
    }

    const selectedOption = filteredAttribute.options.find((option: IAttributeOptions) => option.id === queryParamValue);

    if (!selectedOption || empty(selectedOption)) {
        return filteredAttribute.options.find((option: IAttributeOptions) => option)?.id || '';
    }

    return selectedOption.id;
};

export const useProductConfiguration = (
    swatchConfig: ISwatchConfig | undefined,
    setSelectedProductInformation: (selectedProductInformation: ProductSelectedInfo) => void,
): UseProductConfigurationReturn => {
    const [selectedAttributes, selectAttributes] = useState<ISelectedAttributes[]>([]);
    const [defaultSelectedMainSwatch, setDefaultSelectedMainSwatch] = useState<ISelectedSwatch | undefined>(undefined);

    const hasConfiguration = !!swatchConfig?.options?.attributes?.length;

    // Memoize the indexed attributes for performance
    const indexedAttribute = useMemo(() => {
        if (!swatchConfig?.options?.attributes) return {};

        const attributes: IAttribute[] = swatchConfig.options.attributes;
        return attributes.reduce((result, attribute: IAttribute) => {
            if (!result[attribute.id]) {
                result[attribute.id] = attribute;
            }
            return result;
        }, {} as Record<string, IAttribute>);
    }, [swatchConfig]);

    // Initialize default selected attributes
    useEffect(() => {
        if (!swatchConfig?.options?.attributes) {
            selectAttributes([]);
            setDefaultSelectedMainSwatch(undefined);
            return;
        }

        const attributes: IAttribute[] = swatchConfig.options.attributes;
        const selectedDefaults: ISelectedAttributes[] = attributes.map(
            (filteredAttribute): ISelectedAttributes => ({
                attributeId: filteredAttribute.id,
                optionId: findDefaultOption(filteredAttribute),
            }),
        );

        selectAttributes(selectedDefaults);

        // Set default main swatch
        const firstSelectedDefault = selectedDefaults.find(
            (defaultSelection: ISelectedAttributes) => defaultSelection && defaultSelection.optionId,
        );

        if (!firstSelectedDefault) {
            return;
        }

        const attributeId = firstSelectedDefault.attributeId.toString();
        const optionId = firstSelectedDefault.optionId.toString();
        const option = indexedAttribute[attributeId]?.options.find((option) => option.id.toString() === optionId);

        if (option) {
            const mainSwatch = {
                attributeId,
                optionId,
                products: option.products,
            };
            setDefaultSelectedMainSwatch(mainSwatch);
        }
    }, [swatchConfig, indexedAttribute]);

    // Calculate secondary attributes based on main swatch selection
    const secondaryAttributes = useMemo((): IAttribute[] => {
        if (!swatchConfig?.options?.attributes || !defaultSelectedMainSwatch) return [];

        const attributes: IAttribute[] = swatchConfig.options.attributes;
        const secondaryAttrs: IAttribute[] = [...attributes];

        // Find secondary swatch selection
        let defaultSelectedSecondarySwatch: ISelectedSwatch | undefined;
        const secondSelectedDefault = selectedAttributes.find(
            (defaultSelection: ISelectedAttributes) =>
                defaultSelection.attributeId !== defaultSelectedMainSwatch?.attributeId,
        );

        if (!secondSelectedDefault) {
            return secondaryAttrs;
        }

        const attribute = indexedAttribute[secondSelectedDefault.attributeId.toString()];
        if (attribute?.options) {
            const secondOption = attribute.options.find(
                (option) => option.id.toString() === secondSelectedDefault.optionId.toString(),
            );

            if (secondOption) {
                defaultSelectedSecondarySwatch = {
                    attributeId: secondSelectedDefault.attributeId.toString(),
                    optionId: secondSelectedDefault.optionId.toString(),
                    products: secondOption.products,
                };
            }
        }

        // Filter secondary attributes based on main swatch selection
        return secondaryAttrs.map((secondarySwatch: IAttribute) => {
            if (secondarySwatch.id === defaultSelectedMainSwatch?.attributeId) {
                return secondarySwatch;
            }

            const swatchOptions = secondarySwatch.options.filter((swatchOption) => {
                const swatchProducts = swatchOption.products || [];
                const mainProducts = defaultSelectedMainSwatch?.products || [];
                const secondaryProducts = defaultSelectedSecondarySwatch?.products || [];

                return (
                    swatchProducts.filter(
                        (value) =>
                            mainProducts.includes(value) &&
                            (!defaultSelectedSecondarySwatch ||
                                secondarySwatch.id === defaultSelectedSecondarySwatch?.attributeId ||
                                secondaryProducts.includes(value)),
                    ).length > 0
                );
            });

            return { ...secondarySwatch, options: swatchOptions };
        });
    }, [swatchConfig, defaultSelectedMainSwatch, selectedAttributes, indexedAttribute]);

    // Check if product is properly configured
    const isConfigured = useMemo(() => {
        if (!hasConfiguration) return true;

        return selectedAttributes.every((attr) => attr.optionId && attr.optionId !== '');
    }, [hasConfiguration, selectedAttributes]);

    return {
        selectedAttributes,
        selectAttributes,
        defaultSelectedMainSwatch,
        setDefaultSelectedMainSwatch,
        secondaryAttributes,
        isConfigured,
        hasConfiguration,
        setSelectedProductInformation,
    };
};
